import {
  Popover,
  PopoverProps,
  MenuTrigger,
  Menu,
  MenuTriggerProps,
  MenuProps,
} from "react-aria-components";
import cx from "classnames";
import { ReactNode } from "react";

import styles from "./styles.module.css";

export type DropdownMenuProps<T> = {
  children: ReactNode;
  triggerComponent: ReactNode;
  placement?: PopoverProps["placement"];
  isOpen?: boolean;
  onOpenChange?: MenuTriggerProps["onOpenChange"];
  className?: string;
  isNonModal?: PopoverProps["isNonModal"];
  shouldCloseOnInteractOutside?: PopoverProps["shouldCloseOnInteractOutside"];
  header?: ReactNode;
} & MenuProps<T>;

const DropdownMenu = <T extends object>({
  children,
  triggerComponent,
  placement = "bottom left",
  isOpen,
  onOpenChange,
  className,
  isNonModal,
  shouldCloseOnInteractOutside,
  header,
  autoFocus,
  ...menuProps
}: DropdownMenuProps<T>) => {
  // When a header is present, default autoFocus to false so the header's own
  // autoFocus element (e.g. a search input) is not overridden by the Menu.
  const resolvedAutoFocus = header !== undefined ? (autoFocus ?? false) : autoFocus;

  return (
    <MenuTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      {triggerComponent}
      <Popover
        className={({ defaultClassName }) => cx(defaultClassName, styles.popover)}
        placement={placement}
        isNonModal={isNonModal}
        shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
      >
        {header ? (
          <div className={styles.dropdownContent}>
            {header}
            <Menu
              {...menuProps}
              autoFocus={resolvedAutoFocus}
              className={cx(styles.menuWithHeader, className)}
            >
              {children}
            </Menu>
          </div>
        ) : (
          <Menu {...menuProps} autoFocus={resolvedAutoFocus} className={cx(styles.menu, className)}>
            {children}
          </Menu>
        )}
      </Popover>
    </MenuTrigger>
  );
};

DropdownMenu.displayName = "DS.DropdownMenu";

export default DropdownMenu;
